import { useEffect, useRef, useState } from "react"

const POLL_MS = 30_000

// Polls /api/v1/public/promos/:code on a 30s interval and exposes the
// current spots_remaining / spots_total / status. Initial values come
// from props (server-rendered into the HAML data attributes) so the
// page renders correct values on first paint; the hook then keeps
// them fresh as new shops sign up while the visitor is on the page.
// On fetch failure, retains the last good snapshot.
export default function usePromoCounter(code, initial = {}) {
  const [snapshot, setSnapshot] = useState({
    spotsRemaining: initial.spotsRemaining ?? null,
    spotsTotal: initial.spotsTotal ?? null,
    status: initial.status ?? "open",
    error: null
  })
  const cancelledRef = useRef(false)

  useEffect(() => {
    if (!code) return
    cancelledRef.current = false

    const url = `/api/v1/public/promos/${encodeURIComponent(code)}`
    const fetchOnce = async () => {
      try {
        const res = await fetch(url, { headers: { Accept: "application/json" } })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const json = await res.json()
        if (cancelledRef.current) return

        setSnapshot({
          spotsRemaining: json.spots_remaining,
          spotsTotal: json.spots_total,
          status: json.status,
          error: null
        })
      } catch (err) {
        if (cancelledRef.current) return
        setSnapshot((prev) => ({ ...prev, error: err.message }))
      }
    }

    fetchOnce()
    const interval = setInterval(fetchOnce, POLL_MS)
    return () => {
      cancelledRef.current = true
      clearInterval(interval)
    }
  }, [code])

  return snapshot
}
